import React, { useEffect, useState } from 'react';
import {
    AppliedFilter,
    OutgoingFilter,
    SortOption,
    Project,
    UserDetails,
} from '../types';
import { ProjectLibraryContext } from '../contexts/ProjectLibraryContext';
import { useInterval } from '../hooks/useInterval';
import { useAuth } from '@clerk/clerk-react';
import { fetchProjectLibraryData } from '../utils/backendServices';

export const sortOptions: SortOption[] = [
    {
        label: 'Newest first',
        variable: 'date',
        sortOrder: 'descending',
    },
    {
        label: 'Oldest first',
        variable: 'date',
        sortOrder: 'ascending',
    },
];
const convertSortOrder = (
    order: 'ascending' | 'descending',
): 'ASC' | 'DESC' => {
    switch (order) {
        case 'ascending':
            return 'ASC';
        case 'descending':
            return 'DESC';
        default:
            return 'ASC';
    }
};

interface ProjectLibraryContextProviderProps {
    children: React.ReactNode;
}
const ProjectLibraryContextProvider: React.FC<
    ProjectLibraryContextProviderProps
> = ({ children }) => {
    const [projects, setProjects] = useState<Project[]>([]);
    const [appliedFilters, setAppliedFilters] = useState<AppliedFilter[]>([]);
    const [searchString, setSearchString] = useState('');
    const [numItems, setNumItems] = useState<number>(60);
    const [prevPage, setPrevPage] = useState<number | null>(null);
    const [nextPage, setNextPage] = useState<number | null>(null);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [totalPages, setTotalPages] = useState<number>(0);
    const [filterCounts, setFilterCounts] = useState<OutgoingFilter[]>([]);
    const [userDetailsMapping, setUserDetailsMapping] = useState<{
        [user_id: string]: UserDetails | null;
    }>({});
    const [appliedSortOption, setAppliedSortOption] = useState<SortOption>(
        sortOptions[0],
    );
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const [firstRequestSuccessful, setFirstRequestSuccessful] =
        useState<boolean>(false);

    const { orgId } = useAuth();

    const state = {
        currentPage,
        totalPages,
        prevPage,
        nextPage,
        numItems,
        appliedSortOption,
        sortOptions,
        appliedFilters,
        filterCounts,
        projects,
        userDetailsMapping,
        searchString,
        isLoading,
    };

    const setters = {
        setCurrentPage,
        setTotalPages,
        setPrevPage,
        setNextPage,
        setNumItems,
        setAppliedSortOption,
        setAppliedFilters,
        setFilterCounts,
        setProjects,
        setUserDetailsMapping,
        setSearchString,
        setIsLoading,
    };

    const handleProjectLibraryRequest = async (
        newPageNumber = 1,
        showSpinner = true,
    ) => {
        try {
            if (showSpinner) {
                setIsLoading(true);
            }
            const sortField = state.appliedSortOption.variable;
            const sortOrderValue = appliedSortOption.sortOrder;
            const sqlSortOrder = convertSortOrder(sortOrderValue);
            const pageNumber = newPageNumber;
            const params: {
                pageNumber: number;
                numItems: string;
                filters: AppliedFilter[];
                sortField: string;
                sortOrder: 'ASC' | 'DESC';
                search?: string;
            } = {
                pageNumber: pageNumber,
                numItems: numItems.toString(),
                filters: appliedFilters,
                sortField: sortField,
                sortOrder: sqlSortOrder,
            };

            if (searchString.length > 0) {
                params.search = searchString;
            }
            setCurrentPage(pageNumber);
            setAppliedFilters(appliedFilters);
            const response = await fetchProjectLibraryData(params);
            setProjects(response.projects);
            setPrevPage(response.prev_page);
            setNextPage(response.next_page);
            setTotalPages(response.total_pages);
            setUserDetailsMapping(response.user_details_mapping);
            setFilterCounts(response.outgoing_filters);
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        // if (organization) {
        handleProjectLibraryRequest()
            .then(() => {
                setFirstRequestSuccessful(true);
            })
            .catch((error) => {
                console.log(error);
            });
        //
    }, [appliedFilters, searchString, appliedSortOption, currentPage, orgId]);

    useInterval(() => {
        handleProjectLibraryRequest(currentPage, false).catch((error) => {
            console.log(error);
        });
    }, 60000);

    const refresh = (firstPage = false, showSpinner = true) => {
        const newPageNumber = firstPage ? 1 : currentPage;
        return handleProjectLibraryRequest(newPageNumber, showSpinner);
    };

    return (
        <ProjectLibraryContext.Provider value={{ state, setters, refresh }}>
            {children}
        </ProjectLibraryContext.Provider>
    );
};

export default ProjectLibraryContextProvider;
